import React, { useMemo, useState } from "react";

import {
  ACCESS_LABEL,
  ORG_DEFAULT,
  ORG_DEFAULT_COPY,
  OUTCOME,
  OUTCOME_LABEL,
  RESOLUTION_COPY,
  formatCount,
  selectionFor,
} from "../encompassConstants";
import { InfoDot } from "./InfoTip";

const isAddyStaff = (subject) => {
  const email = String(subject.email || "").toLowerCase();

  return email.endsWith("@addy-ai.com") || email.endsWith("@addy.com");
};

/**
 * What a person opens today, as far as Encompass decides it.
 *
 * The null case is the one worth being careful about: it does NOT mean "sees
 * everything", it means Encompass has no say. Rendering it as an access level
 * would tell an operator this screen is deciding something when it is not.
 */
function AccessPill({ access }) {
  if (!access) {
    return (
      <span className="integrations-people-note">
        Whatever their Addy role allows
      </span>
    );
  }

  // `team` reads as neither of the extremes on purpose: green would say "sees
  // everything" and amber would say "restricted", and it is the middle answer.
  const tone = access === "all" ? "all" : access === "own" ? "own" : "ok";

  return (
    <span className={`integrations-people-pill ${tone}`}>
      {ACCESS_LABEL[access]}
    </span>
  );
}

function PersonRow({ subject, disabled, busy, onSelect }) {
  const [showWhy, setShowWhy] = useState(false);
  // A decision left behind by a previous owner governs nothing, and overwriting
  // it from this row would silently re-bind it to whoever owns the org now.
  const canManage = subject.decision?.ownerUidMatches !== false;
  const noLoans = subject.loansOn === 0;
  // A grant this screen cannot deliver, because the Addy role scopes them
  // anyway. Said on the row rather than buried in "Why?", since it is the
  // difference between the control working and appearing to.
  const overriddenByAddyRole =
    (subject.resolution?.access === "all" ||
      subject.resolution?.access === "team") &&
    subject.addyViewScope === "own";

  return (
    <div className="integrations-people-row">
      <div className="integrations-people-who">
        <div className="integrations-people-name">
          {subject.name || subject.email || "Unknown"}
          {subject.subjectType === "owner" ? (
            <span className="integrations-people-tag">Owner</span>
          ) : null}
        </div>
        <div className="integrations-people-sub">
          {subject.email}
          {subject.addyRole ? ` · ${subject.addyRole}` : ""}
        </div>
      </div>

      <div className={`integrations-people-count ${noLoans ? "is-zero" : ""}`}>
        {formatCount(subject.loansOn)}
      </div>

      <div className="integrations-people-count">
        {formatCount(subject.loansCanSee)}
      </div>

      <div className="integrations-people-cell">
        <AccessPill access={subject.access} />
        {overriddenByAddyRole ? (
          <span className="integrations-people-note">
            Their Addy role scopes them anyway
          </span>
        ) : null}
      </div>

      <div className="integrations-people-cell">
        {canManage ? (
          <select
            className="integrations-select"
            value={subject.opsOutcome || OUTCOME.FOLLOW}
            disabled={disabled || busy}
            aria-label={`What ${subject.name || subject.email} can open`}
            onChange={(event) =>
              onSelect(subject, selectionFor(event.target.value))
            }
          >
            {[OUTCOME.FOLLOW, OUTCOME.ALL, OUTCOME.TEAM, OUTCOME.OWN].map(
              (outcome) => (
                <option key={outcome} value={outcome}>
                  {OUTCOME_LABEL[outcome]}
                </option>
              ),
            )}
          </select>
        ) : (
          <select className="integrations-select" disabled value="none">
            <option value="none">Cannot be changed</option>
          </select>
        )}
        <button
          type="button"
          className="integrations-vis-link"
          aria-expanded={showWhy}
          onClick={() => setShowWhy((shown) => !shown)}
        >
          {showWhy ? "Hide why" : "Why?"}
        </button>
      </div>

      {showWhy ? (
        <div className="integrations-people-why">
          <span>
            <b>Decided by:</b>{" "}
            {RESOLUTION_COPY[subject.resolution?.source] ||
              "Nothing on this screen"}
          </span>
          <span>
            <b>Role in Addy:</b> {subject.addyRole || "none"}
            {subject.addyViewScope === "own"
              ? " (own loans only; change it on the team page)"
              : subject.addyViewScope === "all"
                ? " (can open every loan)"
                : ""}
          </span>
          {subject.decision?.ownerUidMatches === false ? (
            <span>
              A previous owner was decided about here. That decision no longer
              applies, and this organization has since changed hands.
            </span>
          ) : null}
          {overriddenByAddyRole ? (
            <span>
              {ACCESS_LABEL[subject.resolution?.access]} was chosen here, but
              their Addy role limits them to their own loans and the narrower of
              the two always wins. Widen the Addy role on the team page to make
              this take effect.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Check access": one person, one loan, and the verdict the loan board itself
 * would give — not a second opinion computed here.
 */
function CheckAccess({ subjects, onExplain }) {
  const [subjectId, setSubjectId] = useState(() =>
    subjects[0] ? subjectKey(subjects[0]) : "",
  );
  const [loan, setLoan] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const subject =
    subjects.find((entry) => subjectKey(entry) === subjectId) || subjects[0];
  const canCheck = Boolean(subject) && loan.trim().length > 0 && !checking;

  const check = async () => {
    if (!canCheck) {
      return;
    }

    setChecking(true);
    setError(null);
    try {
      const explanation = await onExplain({
        subjectType: subject.subjectType,
        memberId: subject.memberId || null,
        loan: loan.trim(),
      });
      setResult({ subject, explanation });
    } catch (checkError) {
      setResult(null);
      setError(checkError?.message || "Could not check access");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="integrations-access-check">
      <div className="integrations-access-check-head">
        <b>Check access</b>
        <span>Why someone can or cannot open a loan</span>
      </div>
      <div className="integrations-access-check-row">
        <select
          className="integrations-select"
          aria-label="Person to check"
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
        >
          {subjects.map((entry) => (
            <option key={subjectKey(entry)} value={subjectKey(entry)}>
              {entry.name || entry.email}
            </option>
          ))}
        </select>
        <span className="integrations-people-note">opening</span>
        <input
          className="integrations-input"
          placeholder="Loan number, Addy loan id or Encompass id"
          aria-label="Loan to check"
          value={loan}
          onChange={(event) => setLoan(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              check();
            }
          }}
        />
        <button
          type="button"
          className={`integrations-btn integrations-btn-secondary ${checking ? "is-loading" : ""}`}
          disabled={!canCheck}
          onClick={check}
        >
          {checking ? "Checking…" : "Check"}
        </button>
      </div>

      {error ? <div className="integrations-result err">{error}</div> : null}

      {result ? <CheckAccessResult result={result} /> : null}
    </div>
  );
}

function CheckAccessResult({ result }) {
  const { explanation } = result;

  if (!explanation.found) {
    return (
      <div className="integrations-access-check-out">
        <b>That loan is not in Addy.</b>
        <span>
          {" "}
          Nothing here can grant access to a loan Encompass never delivered.
        </span>
      </div>
    );
  }

  const { loan, person, canOpen } = explanation;

  return (
    <div className="integrations-access-check-out">
      <b>
        {canOpen === null
          ? "The board's verdict could not be read."
          : canOpen
            ? "Yes — they can open it."
            : "No — they cannot open it."}
      </b>
      {canOpen === false && !person.onLoan ? (
        <span>
          {" "}
          They are not on this loan, and they are limited to loans they are on.
        </span>
      ) : null}
      <div className="integrations-people-note" style={{ marginTop: 4 }}>
        {loan.loanNumber ? `Loan ${loan.loanNumber}` : `Loan ${loan.id}`}
        {loan.stage ? ` · ${loan.stage}` : ""}
        {" · Team on the loan: "}
        {loan.teamEmails.length > 0
          ? loan.teamEmails.join(", ")
          : "nobody recorded"}
      </div>
    </div>
  );
}

export function subjectKey(subject) {
  return subject.subjectType === "owner"
    ? "owner"
    : `member:${subject.memberId}`;
}

/**
 * "People": what each person in an Encompass organization can open.
 *
 * Every loan the lender's Encompass instance delivers is already in Addy —
 * nothing is filtered at import — so this screen is the only thing deciding who
 * sees whose loans. It is laid out to say that plainly: the organization's own
 * default sits above the table, because for most people it is what is actually
 * deciding, and a per-person row only exists once somebody overrides it.
 */
export function EncompassPeopleTab({
  people,
  loading,
  error,
  busyKey,
  disabled,
  savingDefault,
  savingAutoSync,
  onSelect,
  onChangeDefault,
  onToggleAutoSync,
  onRetry,
  onExplain,
}) {
  const [search, setSearch] = useState("");

  // Addy staff sit in customer organizations for support; they are never who
  // operations came to look at, so they always sort last. The owner leads.
  const subjects = useMemo(() => {
    if (!people) {
      return [];
    }

    const members = people.members || [];

    return [
      people.owner,
      ...members.filter((subject) => !isAddyStaff(subject)),
      ...members.filter(isAddyStaff),
    ].filter(Boolean);
  }, [people]);

  const counts = useMemo(
    () => ({
      total: subjects.length,
      all: subjects.filter((subject) => subject.access === "all").length,
      team: subjects.filter((subject) => subject.access === "team").length,
      own: subjects.filter((subject) => subject.access === "own").length,
      attention: subjects.filter((subject) => subject.needsAttention).length,
    }),
    [subjects],
  );

  const term = search.trim().toLowerCase();
  const visible = subjects.filter(
    (subject) =>
      !term ||
      `${subject.name || ""} ${subject.email || ""}`
        .toLowerCase()
        .includes(term),
  );

  if (loading) {
    return <div className="integrations-empty">Loading…</div>;
  }

  if (error) {
    // `fetchWithFallback` throws on any non-2xx, so an environment whose backend
    // predates these endpoints arrives here as a 404. Expected during rollout,
    // so it gets an inline notice rather than a toast.
    const looksUnavailable = /404|not found/i.test(error);

    return (
      <div className="integrations-result err">
        {looksUnavailable
          ? "This is not available on this environment yet. It needs the backend release that adds it."
          : error}
        {onRetry && !looksUnavailable ? (
          <button
            type="button"
            className="integrations-btn integrations-btn-ghost integrations-btn-small"
            style={{ marginLeft: 10 }}
            onClick={onRetry}
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (!people) {
    return null;
  }

  const organization = people.organization || {};
  const orgDefault =
    organization.loanVisibilityDefault || ORG_DEFAULT.FOLLOW_ADDY_ROLE;
  const isWideOpen = orgDefault === ORG_DEFAULT.FOLLOW_ADDY_ROLE;

  return (
    <div className="integrations-people">
      {!organization.encompassEnabled ? (
        <div className="integrations-result">
          Encompass is not enabled for this organization, so nothing here is
          limiting anyone yet.
        </div>
      ) : null}

      {organization.autoSyncEnabled && isWideOpen ? (
        <div className="integrations-save-note warn">
          <span className="integrations-note-mark" aria-hidden="true">
            !
          </span>
          <span>
            Auto-sync is on and nothing is narrowing who sees the result. Every
            loan this lender's Encompass instance delivers is visible to anyone
            whose Addy role can open every loan. If the instance covers branches
            that are not this organization, set the default below to{" "}
            <b>every loan our team is on</b>.
          </span>
        </div>
      ) : null}

      <div className="integrations-vis-org-default">
        <div>
          <b>Everyone without a decision of their own</b>
          <p className="integrations-field-hint">
            {ORG_DEFAULT_COPY[orgDefault]?.detail}
          </p>
        </div>
        <select
          className="integrations-select"
          aria-label="What everyone without a decision of their own sees"
          value={orgDefault}
          disabled={disabled || savingDefault}
          onChange={(event) => onChangeDefault(event.target.value)}
        >
          {(
            organization.availableDefaults || Object.keys(ORG_DEFAULT_COPY)
          ).map((value) => (
            <option key={value} value={value}>
              {ORG_DEFAULT_COPY[value]?.label || value}
            </option>
          ))}
        </select>
      </div>

      <div className="integrations-vis-org-default">
        <div>
          <b>Auto-sync from Encompass</b>
          <p className="integrations-field-hint">
            Whether loans ordered by an org-wide rule are accepted. This changes
            what arrives, never who sees it.
          </p>
        </div>
        <button
          type="button"
          className={`integrations-btn ${organization.autoSyncEnabled ? "integrations-btn-ghost" : "integrations-btn-secondary"}`}
          disabled={disabled || savingAutoSync}
          onClick={() => onToggleAutoSync(!organization.autoSyncEnabled)}
        >
          {organization.autoSyncEnabled ? "Turn off" : "Turn on"}
        </button>
      </div>

      <div className="integrations-vis-summary">
        <div className="integrations-vis-cell">
          <b>{counts.total}</b>
          <span>people</span>
        </div>
        <div className="integrations-vis-cell ok">
          <b>{counts.all}</b>
          <span>open every loan</span>
        </div>
        <div className="integrations-vis-cell">
          <b>{counts.team}</b>
          <span>our team&rsquo;s loans</span>
        </div>
        <div className="integrations-vis-cell">
          <b>{counts.own}</b>
          <span>own loans only</span>
        </div>
        <div className="integrations-vis-cell warn">
          <b>{counts.attention}</b>
          <span>need attention</span>
        </div>
      </div>

      <div className="integrations-vis-toolbar">
        <input
          type="search"
          className="integrations-input"
          placeholder="Search by name or email"
          aria-label="Search for a person"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="integrations-people-table">
        <div className="integrations-people-head">
          <span>Person</span>
          <span>
            Loans
            <InfoDot
              summary="How many loans in Addy this person is on."
              detail="Read from the same list the loan board queries, so this is exactly what they would see if they were limited to their own loans."
            />
          </span>
          <span>
            Can see
            <InfoDot
              summary="How many loans this person can actually open today."
              detail="Their own loans, the team's, or every one — whichever &quot;Can open&quot; resolves to, counted, including the Addy role's narrowing. The two columns can never disagree."
            />
          </span>
          <span>
            Can open
            <InfoDot
              summary="What this person opens on the loan board today."
              detail='Their own decision here wins; failing that the organization&apos;s default decides. Their Addy role can only narrow it further, and decides alone when this screen says nothing. "Every loan our team is on" means every loan naming somebody with an Addy seat here, and hides the branches nobody here works with.'
            />
          </span>
          <span>Change</span>
        </div>

        {visible.map((subject) => (
          <PersonRow
            key={subjectKey(subject)}
            subject={subject}
            disabled={disabled}
            busy={busyKey === subjectKey(subject)}
            onSelect={onSelect}
          />
        ))}
        {visible.length === 0 ? (
          <div className="integrations-people-empty">
            {subjects.length === 0
              ? "Nobody in this organization yet."
              : "Nobody matches that."}
          </div>
        ) : null}
      </div>

      {subjects.length > 0 ? (
        <CheckAccess subjects={subjects} onExplain={onExplain} />
      ) : null}
    </div>
  );
}
